import type { FieldProvenanceDto, MissionAssessmentInterAiGenerationDataDto } from '@@types/api.types';
import {
  FieldedStatus,
  LlmStreamStatus,
  MissionAssessmentChatObjectKind,
  MissionAssessmentFieldName,
  MissionAssessmentInterAiGenerationResponseDto,
  MissionAssessmentInterAiQueryDto,
  MissionAssessmentInterType,
  VerificationConfidence,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { FormSuggestion, useFormSuggestion } from '@components/shared/form-suggestion.component';
import { GenerateJSONButton } from '@components/shared/generate-json-button.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { SelectMultipleField } from '@components/ui/form/select-multiple-field.component';
import { config } from '@config';
import maDefinitions from '@data/ma-definitions.json';
import { cn } from '@lib/utils';
import { useGetCategories } from '@queries/category.query';
import { useGetDigitalIntegrations } from '@queries/digital-integration.query';
import { useGenerateMissionAssessmentInter } from '@queries/mission-assessments.query';
import { useGetOperationalIntegrations } from '@queries/operational-integration.query';
import { useGetPhysicalIntegrations } from '@queries/physical-integration.query';
import { missionAssessmentInterCreateSchema } from '@schemas/mission-assessment.schema';
import { useStore } from '@tanstack/react-form';
import { sectionProvenance } from '@utils/mission-assessment-provenance.util';
import { mapToSelectOptions } from '@utils/select.util';
import { PlusIcon, SparklesIcon, XIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { MISSION_ASSESSMENT_AI_ENDPOINTS } from '../../../../constants/mission-assessment-endpoints.constants';
import { FIELDED_STATUS_LABELS, VERIFICATION_CONFIDENCE_LABELS, countries } from '../../../../utils/constants';
import { FormField } from '../../../shared/form-field.component';
import { FormLabel } from '../../../shared/form-label.component';
import { missionAssessmentDraftFormOptions } from './mission-assessment-form-options';
import { MissionAssessmentGenerateButton } from './mission-assessment-generate-button.component';
import {
  MissionAssessmentInterSuggestionFields,
  MissionAssessmentInteroperabilitySuggestion,
} from './mission-assessment-interoperability-suggestion.component';
import { parseGeneratedInterData } from './mission-assessment-interoperability-suggestion.util';

/**
 * Check if any field within a specific array item has validation errors
 */
const hasValidationErrors = (
  state: { fieldMeta: Record<string, { errors?: string[] }> },
  itemIndex: number,
): boolean => {
  const fieldPaths = [
    `inters[${itemIndex}].name`,
    `inters[${itemIndex}].description`,
    `inters[${itemIndex}].categoryId`,
    `inters[${itemIndex}].subcategoryId`,
    `inters[${itemIndex}].notes`,
  ];

  return fieldPaths.some((path) => {
    const fieldMeta = state.fieldMeta[path as keyof typeof state.fieldMeta];
    return fieldMeta?.errors && fieldMeta.errors.length > 0;
  });
};

const isAIEnabled = config.ai.enabled;

export const MissionAssessmentFormInteroperability = withForm({
  ...missionAssessmentDraftFormOptions,
  props: {
    provenance: undefined as Record<string, FieldProvenanceDto> | null | undefined,
    submitAction: undefined as 'draft' | 'complete' | null | undefined,
    technologyId: undefined as string | undefined,
  },
  render: function Render({ form, provenance, submitAction = null, technologyId }) {
    const {
      acceptSuggestion,
      rejectSuggestion,
      getSuggestion,
      getSuggestionModel,
      getIsGenerating,
      getIsStreaming,
      handleMetadata,
      handleGeneratingStart,
      handleGeneratingEnd,
      handleJsonData,
      handleStreamingEnd,
      handleStreamingStart,
      handleSuggestionStream,
    } = useFormSuggestion<string, MissionAssessmentInterAiGenerationDataDto>();
    const { data: availableCategories, isLoading: isLoadingCategories } = useGetCategories();
    const { data: digitalIntegrations, isLoading: isLoadingDigitalIntegrations } = useGetDigitalIntegrations();
    const { data: operationalIntegrations, isLoading: isLoadingOperationalIntegrations } =
      useGetOperationalIntegrations();
    const { data: physicalIntegrations, isLoading: isLoadingPhysicalIntegrations } = useGetPhysicalIntegrations();

    const { mutateAsync: generateInter } = useGenerateMissionAssessmentInter();

    const handleGenerationSuccess = useCallback(
      (type: MissionAssessmentInterType) => (result: MissionAssessmentInterAiGenerationResponseDto) => {
        const key = `inter_${type}` as string;
        handleJsonData({ key, data: result.data });
        handleMetadata({
          key,
          metadata: {
            actualModel: result.metadata.actualModel,
            logId: result.metadata.logId,
            requestedModel: result.metadata.requestedModel,
            status: LlmStreamStatus.Completed,
          },
        });
      },
      [handleJsonData, handleMetadata],
    );

    const handleGeneratingStartCallback = useCallback(
      (type: MissionAssessmentInterType) => () => {
        const key = `inter_${type}` as string;
        handleGeneratingStart(key);
      },
      [handleGeneratingStart],
    );

    const handleGeneratingEndCallback = useCallback(
      (type: MissionAssessmentInterType) => () => {
        const key = `inter_${type}` as string;
        handleGeneratingEnd(key);
      },
      [handleGeneratingEnd],
    );

    const applyGeneratedInter = useCallback(
      (type: MissionAssessmentInterType, data: MissionAssessmentInterAiGenerationDataDto) => {
        const currentInters = form.state.values.inters || [];
        const newItem = {
          categoryId: data.categoryId || '',
          country: undefined,
          description: data.description || '',
          digitalIntegrationIds: data.digitalIntegrationIds || [],
          fieldedStatus: data.fieldedStatus,
          mrl: data.mrl,
          name: data.name || '',
          notes: data.notes || '',
          operationalIntegrationIds: data.operationalIntegrationIds || [],
          physicalIntegrationIds: data.physicalIntegrationIds || [],
          priorityRationale: data.priorityRationale,
          priorityScore: data.priorityScore,
          reference: data.reference,
          subcategoryId: data.subcategoryId || '',
          trl: data.trl,
          type,
          verificationConfidence: data.verificationConfidence,
          verificationNotes: data.verificationNotes,
        } as const;

        form.setFieldValue('inters', [...currentInters, newItem]);
      },
      [form],
    );

    const handleAcceptJsonSuggestion = useCallback(
      (type: MissionAssessmentInterType) => {
        const key = `inter_${type}` as string;
        acceptSuggestion(key, (data: string | MissionAssessmentInterAiGenerationDataDto) => {
          if (typeof data === 'object') {
            applyGeneratedInter(type, data);
          }
        });
      },
      [acceptSuggestion, applyGeneratedInter],
    );

    const getTextSuggestion = (key: string): string | undefined => {
      const value = getSuggestion(key);
      return typeof value === 'string' ? value : undefined;
    };

    const acceptInterTextSuggestion = (key: string, originalIndex: number, field: 'description' | 'notes') => {
      acceptSuggestion(key, (data) => {
        if (typeof data !== 'string') return;
        if (field === 'description') {
          form.setFieldValue(`inters[${originalIndex}].description`, data);
        } else {
          form.setFieldValue(`inters[${originalIndex}].notes`, data);
        }
      });
    };

    const categoryOptions = useMemo(
      () =>
        mapToSelectOptions(availableCategories).map((category) => ({
          ...category,
          subcategories: mapToSelectOptions(category.subcategories),
        })),
      [availableCategories],
    );

    const digitalIntegrationOptions = useMemo(() => mapToSelectOptions(digitalIntegrations), [digitalIntegrations]);
    const operationalIntegrationOptions = useMemo(
      () => mapToSelectOptions(operationalIntegrations),
      [operationalIntegrations],
    );

    const physicalIntegrationOptions = useMemo(() => mapToSelectOptions(physicalIntegrations), [physicalIntegrations]);

    const getSubcategoryOptions = (categoryId?: string) => {
      if (!categoryId) return [];
      const category = categoryOptions.find((cat) => cat.id === categoryId);
      if (!category) return [];
      return category.subcategories || [];
    };

    const sections = [
      {
        title: 'Interchangeable with',
        type: MissionAssessmentInterType.INTERCHANGEABLE,
        tooltip: maDefinitions?.interchangeable,
      },
      {
        title: 'Interoperable with',
        type: MissionAssessmentInterType.INTEROPERABLE,
        tooltip: maDefinitions?.interoperable,
      },
    ];

    const inters = useStore(form.store, (state) => state.values.inters);
    const isComplete = submitAction === 'complete';

    return (
      <div className="space-y-8">
        {sections.map((section) => (
          <div key={section.type}>
            <div className="mb-2 flex items-center gap-2">
              <FormLabel
                label={section.title}
                tooltip={section.tooltip}
              />
              <FieldProvenanceBadge
                context="form"
                provenance={sectionProvenance(provenance, section.type)}
              />
            </div>

            <form.AppField
              name="inters"
              mode="array"
            >
              {(field) => {
                const itemsForSection = field.state.value
                  ?.map((item, index) => ({ ...item, originalIndex: index }))
                  .filter((item) => item.type === section.type);

                return (
                  <div className="flex flex-col gap-y-3">
                    {itemsForSection?.map((item) => {
                      const descriptionFieldName =
                        section.type === MissionAssessmentInterType.INTEROPERABLE
                          ? MissionAssessmentFieldName.INTEROPERABLE_DESCRIPTION
                          : MissionAssessmentFieldName.INTERCHANGEABLE_DESCRIPTION;
                      const notesFieldName =
                        section.type === MissionAssessmentInterType.INTEROPERABLE
                          ? MissionAssessmentFieldName.INTEROPERABLE_NOTES
                          : MissionAssessmentFieldName.INTERCHANGEABLE_NOTES;
                      const descriptionSuggestionKey = `inter_text_${section.type}_description_${item.originalIndex}`;
                      const notesSuggestionKey = `inter_text_${section.type}_notes_${item.originalIndex}`;

                      return (
                        <form.Subscribe
                          key={item.originalIndex}
                          selector={(state) => hasValidationErrors(state, item.originalIndex)}
                        >
                          {(hasErrors) => (
                            <div
                              className={cn(
                                'p-4 border rounded-md flex flex-col gap-4 bg-white',
                                hasErrors ? 'border-red-500' : 'border-gray-200',
                              )}
                            >
                              <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <Accordion
                                    type="multiple"
                                    defaultValue={['main']}
                                    className="space-y-2"
                                  >
                                    {/* Main Information Section */}
                                    <AccordionItem
                                      value="main"
                                      className="border rounded-md bg-white"
                                    >
                                      <AccordionTrigger className="px-4 hover:no-underline">
                                        <span className="text-sm font-medium">Main Information</span>
                                      </AccordionTrigger>
                                      <AccordionContent className="px-4 pb-4">
                                        <div className="space-y-4">
                                          {/* Name, Reference, and Country Row */}
                                          <div className="grid gap-4 md:grid-cols-3">
                                            <form.AppField
                                              name={`inters[${item.originalIndex}].name`}
                                              validators={
                                                isComplete
                                                  ? {
                                                      onBlur: missionAssessmentInterCreateSchema.shape.name,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {(nameField) => (
                                                <FormField>
                                                  <FormLabel
                                                    label="Name"
                                                    className="w-auto"
                                                  />

                                                  <nameField.TextField placeholder="Technology name" />
                                                </FormField>
                                              )}
                                            </form.AppField>

                                            <form.AppField
                                              name={`inters[${item.originalIndex}].reference`}
                                              validators={
                                                isComplete
                                                  ? {
                                                      onBlur: missionAssessmentInterCreateSchema.shape.reference,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {(referenceField) => (
                                                <FormField>
                                                  <FormLabel
                                                    label="Reference"
                                                    className="w-auto"
                                                  />
                                                  <referenceField.TextField placeholder="Reference or source" />
                                                </FormField>
                                              )}
                                            </form.AppField>

                                            <form.AppField
                                              name={`inters[${item.originalIndex}].country`}
                                              validators={
                                                isComplete
                                                  ? {
                                                      onChange: missionAssessmentInterCreateSchema.shape.country,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {(countryField) => (
                                                <FormField>
                                                  <FormLabel
                                                    label="Country"
                                                    className="w-auto"
                                                  />
                                                  <countryField.SelectPopoverField
                                                    emptyMessage="No country found"
                                                    formatValue={(option) => (option ? option.label : 'Select country')}
                                                    options={countries}
                                                    placeholder="Select country"
                                                    searchPlaceholder="Search country..."
                                                  />
                                                </FormField>
                                              )}
                                            </form.AppField>
                                          </div>

                                          <form.AppField
                                            name={`inters[${item.originalIndex}].description`}
                                            validators={
                                              isComplete
                                                ? {
                                                    onBlur: missionAssessmentInterCreateSchema.shape.description,
                                                  }
                                                : {}
                                            }
                                          >
                                            {(descField) => (
                                              <FormField>
                                                <FormLabel
                                                  label="Description"
                                                  className="w-auto"
                                                />

                                                <descField.TextAreaField
                                                  generateButton={
                                                    technologyId &&
                                                    isAIEnabled && (
                                                      <form.Subscribe
                                                        selector={(state) => ({
                                                          name: state.values.inters?.[item.originalIndex]?.name,
                                                          reference:
                                                            state.values.inters?.[item.originalIndex]?.reference,
                                                        })}
                                                      >
                                                        {(interContext) => (
                                                          <MissionAssessmentGenerateButton
                                                            fieldName={descriptionFieldName}
                                                            interContext={interContext}
                                                            onAccept={(text) =>
                                                              form.setFieldValue(
                                                                `inters[${item.originalIndex}].description`,
                                                                text,
                                                              )
                                                            }
                                                            onMetadata={(metadata) =>
                                                              handleMetadata({
                                                                key: descriptionSuggestionKey,
                                                                metadata,
                                                              })
                                                            }
                                                            onStream={(text) =>
                                                              handleSuggestionStream({
                                                                key: descriptionSuggestionKey,
                                                                text,
                                                              })
                                                            }
                                                            onStreamingStart={() =>
                                                              handleStreamingStart(descriptionSuggestionKey)
                                                            }
                                                            onStreamingEnd={() =>
                                                              handleStreamingEnd(descriptionSuggestionKey)
                                                            }
                                                            technologyId={technologyId}
                                                          />
                                                        )}
                                                      </form.Subscribe>
                                                    )
                                                  }
                                                  placeholder="Technology description"
                                                  rows={2}
                                                />

                                                <FormSuggestion
                                                  isStreaming={getIsStreaming(descriptionSuggestionKey)}
                                                  model={getSuggestionModel(descriptionSuggestionKey)}
                                                  onAccept={() =>
                                                    acceptInterTextSuggestion(
                                                      descriptionSuggestionKey,
                                                      item.originalIndex,
                                                      'description',
                                                    )
                                                  }
                                                  onReject={() => rejectSuggestion(descriptionSuggestionKey)}
                                                  rejectButtonProps={{ variant: 'destructive' }}
                                                  suggestion={getTextSuggestion(descriptionSuggestionKey)}
                                                />
                                              </FormField>
                                            )}
                                          </form.AppField>

                                          <div className="flex gap-4 min-w-0">
                                            <form.AppField
                                              name={`inters[${item.originalIndex}].categoryId`}
                                              validators={
                                                isComplete
                                                  ? {
                                                      onChange: missionAssessmentInterCreateSchema.shape.categoryId,
                                                    }
                                                  : {}
                                              }
                                              listeners={{
                                                onChange: ({ value: newCategoryId }) => {
                                                  const currentInter = inters?.[item.originalIndex];
                                                  const previousCategoryId = currentInter?.categoryId;
                                                  if (newCategoryId !== previousCategoryId) {
                                                    form.setFieldValue(
                                                      `inters[${item.originalIndex}].subcategoryId`,
                                                      '',
                                                    );
                                                  }
                                                },
                                              }}
                                            >
                                              {(categoryField) => (
                                                <div className="flex-1 min-w-0">
                                                  <categoryField.SelectPopoverField
                                                    label="Category"
                                                    required={isComplete}
                                                    disabled={isLoadingCategories}
                                                    emptyMessage="No technology categories found."
                                                    formatValue={(option) =>
                                                      option ? option.label : 'Select technology category'
                                                    }
                                                    options={categoryOptions}
                                                    placeholder="Select technology category"
                                                    searchPlaceholder="Search technology categories..."
                                                    triggerClassName="w-full truncate"
                                                  />
                                                </div>
                                              )}
                                            </form.AppField>

                                            <form.Subscribe
                                              selector={(state) => state.values.inters?.[item.originalIndex]}
                                            >
                                              {(inter) => (
                                                <form.AppField
                                                  name={`inters[${item.originalIndex}].subcategoryId`}
                                                  validators={
                                                    isComplete
                                                      ? {
                                                          onChange:
                                                            missionAssessmentInterCreateSchema.shape.subcategoryId,
                                                        }
                                                      : {}
                                                  }
                                                >
                                                  {(subcategoryField) => {
                                                    const subcategoryOptions = getSubcategoryOptions(inter?.categoryId);
                                                    return (
                                                      <div className="flex-1 min-w-0">
                                                        <subcategoryField.SelectPopoverField
                                                          label="Subcategory"
                                                          required={isComplete}
                                                          disabled={
                                                            isLoadingCategories ||
                                                            !inter?.categoryId ||
                                                            subcategoryOptions.length === 0
                                                          }
                                                          emptyMessage={
                                                            inter?.categoryId
                                                              ? 'No subcategories found for this category.'
                                                              : 'Please select a category first.'
                                                          }
                                                          formatValue={(option) =>
                                                            option ? option.label : 'Select technology subcategory'
                                                          }
                                                          options={subcategoryOptions}
                                                          placeholder="Select technology subcategory"
                                                          searchPlaceholder="Search technology subcategories..."
                                                          triggerClassName="w-full truncate"
                                                        />
                                                      </div>
                                                    );
                                                  }}
                                                </form.AppField>
                                              )}
                                            </form.Subscribe>
                                          </div>

                                          <form.AppField
                                            name={`inters[${item.originalIndex}].notes`}
                                            validators={
                                              isComplete
                                                ? {
                                                    onBlur: missionAssessmentInterCreateSchema.shape.notes,
                                                  }
                                                : {}
                                            }
                                          >
                                            {(notesField) => (
                                              <FormField>
                                                <FormLabel
                                                  label="Notes"
                                                  className="w-auto"
                                                />

                                                <notesField.TextAreaField
                                                  generateButton={
                                                    technologyId &&
                                                    isAIEnabled && (
                                                      <form.Subscribe
                                                        selector={(state) => ({
                                                          name: state.values.inters?.[item.originalIndex]?.name,
                                                          reference:
                                                            state.values.inters?.[item.originalIndex]?.reference,
                                                        })}
                                                      >
                                                        {(interContext) => (
                                                          <MissionAssessmentGenerateButton
                                                            fieldName={notesFieldName}
                                                            interContext={interContext}
                                                            onAccept={(text) =>
                                                              form.setFieldValue(
                                                                `inters[${item.originalIndex}].notes`,
                                                                text,
                                                              )
                                                            }
                                                            onMetadata={(metadata) =>
                                                              handleMetadata({ key: notesSuggestionKey, metadata })
                                                            }
                                                            onStream={(text) =>
                                                              handleSuggestionStream({ key: notesSuggestionKey, text })
                                                            }
                                                            onStreamingStart={() =>
                                                              handleStreamingStart(notesSuggestionKey)
                                                            }
                                                            onStreamingEnd={() =>
                                                              handleStreamingEnd(notesSuggestionKey)
                                                            }
                                                            technologyId={technologyId}
                                                          />
                                                        )}
                                                      </form.Subscribe>
                                                    )
                                                  }
                                                  placeholder="Additional notes"
                                                  rows={3}
                                                />

                                                <FormSuggestion
                                                  isStreaming={getIsStreaming(notesSuggestionKey)}
                                                  model={getSuggestionModel(notesSuggestionKey)}
                                                  onAccept={() =>
                                                    acceptInterTextSuggestion(
                                                      notesSuggestionKey,
                                                      item.originalIndex,
                                                      'notes',
                                                    )
                                                  }
                                                  onReject={() => rejectSuggestion(notesSuggestionKey)}
                                                  rejectButtonProps={{ variant: 'destructive' }}
                                                  suggestion={getTextSuggestion(notesSuggestionKey)}
                                                />
                                              </FormField>
                                            )}
                                          </form.AppField>

                                          {/* TRL, MRL, and Fielded Status */}
                                          <div className="grid gap-4 md:grid-cols-3">
                                            <form.AppField
                                              name={`inters[${item.originalIndex}].trl`}
                                              validators={
                                                isComplete
                                                  ? {
                                                      onBlur: missionAssessmentInterCreateSchema.shape.trl,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {(trlField) => (
                                                <FormField>
                                                  <FormLabel
                                                    label="TRL"
                                                    className="w-auto"
                                                  />
                                                  <trlField.TextField
                                                    type="number"
                                                    placeholder="1-9"
                                                    min={1}
                                                    max={9}
                                                  />
                                                </FormField>
                                              )}
                                            </form.AppField>

                                            <form.AppField
                                              name={`inters[${item.originalIndex}].mrl`}
                                              validators={
                                                isComplete
                                                  ? {
                                                      onBlur: missionAssessmentInterCreateSchema.shape.mrl,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {(mrlField) => (
                                                <FormField>
                                                  <FormLabel
                                                    label="MRL"
                                                    className="w-auto"
                                                  />
                                                  <mrlField.TextField
                                                    type="number"
                                                    placeholder="1-10"
                                                    min={1}
                                                    max={10}
                                                  />
                                                </FormField>
                                              )}
                                            </form.AppField>

                                            <form.AppField
                                              name={`inters[${item.originalIndex}].fieldedStatus`}
                                              validators={
                                                isComplete
                                                  ? {
                                                      onChange: missionAssessmentInterCreateSchema.shape.fieldedStatus,
                                                    }
                                                  : {}
                                              }
                                            >
                                              {(fieldedStatusField) => (
                                                <FormField>
                                                  <FormLabel
                                                    label="Fielded Status"
                                                    className="w-auto"
                                                  />
                                                  <fieldedStatusField.SelectPopoverField
                                                    emptyMessage="No fielded status options available."
                                                    formatValue={(option) => {
                                                      if (!option) return 'Select fielded status';

                                                      return (
                                                        FIELDED_STATUS_LABELS[option.value as FieldedStatus] ||
                                                        String(option.value)
                                                      );
                                                    }}
                                                    options={Object.values(FieldedStatus).map((value) => ({
                                                      value,
                                                      label: FIELDED_STATUS_LABELS[value],
                                                    }))}
                                                    placeholder="Select fielded status"
                                                    searchPlaceholder="Search fielded status..."
                                                  />
                                                </FormField>
                                              )}
                                            </form.AppField>
                                          </div>
                                        </div>
                                      </AccordionContent>
                                    </AccordionItem>

                                    {/* Priority Section */}
                                    <AccordionItem
                                      value="priority"
                                      className="border rounded-md bg-white"
                                    >
                                      <AccordionTrigger className="px-4 hover:no-underline">
                                        <span className="text-sm font-medium">Priority</span>
                                      </AccordionTrigger>
                                      <AccordionContent className="px-4 pb-4">
                                        <div className="space-y-4">
                                          <form.AppField
                                            name={`inters[${item.originalIndex}].priorityScore`}
                                            validators={
                                              isComplete
                                                ? {
                                                    onBlur: missionAssessmentInterCreateSchema.shape.priorityScore,
                                                  }
                                                : {}
                                            }
                                          >
                                            {(priorityScoreField) => (
                                              <FormField>
                                                <FormLabel
                                                  label="Priority Score"
                                                  className="w-auto"
                                                />
                                                <priorityScoreField.TextField
                                                  type="number"
                                                  placeholder="1-100"
                                                  min={1}
                                                  max={100}
                                                />
                                              </FormField>
                                            )}
                                          </form.AppField>

                                          <form.AppField
                                            name={`inters[${item.originalIndex}].priorityRationale`}
                                            validators={
                                              isComplete
                                                ? {
                                                    onBlur: missionAssessmentInterCreateSchema.shape.priorityRationale,
                                                  }
                                                : {}
                                            }
                                          >
                                            {(priorityRationaleField) => (
                                              <FormField>
                                                <FormLabel
                                                  label="Priority Rationale"
                                                  className="w-auto"
                                                />
                                                <priorityRationaleField.TextAreaField
                                                  placeholder="Rationale for priority score"
                                                  rows={2}
                                                />
                                              </FormField>
                                            )}
                                          </form.AppField>
                                        </div>
                                      </AccordionContent>
                                    </AccordionItem>

                                    {/* Verification Section */}
                                    <AccordionItem
                                      value="verification"
                                      className="border rounded-md bg-white"
                                    >
                                      <AccordionTrigger className="px-4 hover:no-underline">
                                        <span className="text-sm font-medium">Verification</span>
                                      </AccordionTrigger>
                                      <AccordionContent className="px-4 pb-4">
                                        <div className="space-y-4">
                                          <form.AppField
                                            name={`inters[${item.originalIndex}].verificationConfidence`}
                                            validators={
                                              isComplete
                                                ? {
                                                    onChange:
                                                      missionAssessmentInterCreateSchema.shape.verificationConfidence,
                                                  }
                                                : {}
                                            }
                                          >
                                            {(verificationConfidenceField) => (
                                              <verificationConfidenceField.SelectPopoverField
                                                label="Verification Confidence"
                                                emptyMessage="No verification confidence options available."
                                                formatValue={(option) => {
                                                  if (!option) return 'Select confidence';
                                                  return (
                                                    VERIFICATION_CONFIDENCE_LABELS[
                                                      option.value as VerificationConfidence
                                                    ] || String(option.value)
                                                  );
                                                }}
                                                options={Object.values(VerificationConfidence).map((value) => ({
                                                  value: value,
                                                  label: VERIFICATION_CONFIDENCE_LABELS[value],
                                                }))}
                                                placeholder="Select confidence"
                                                searchPlaceholder="Search confidence..."
                                              />
                                            )}
                                          </form.AppField>

                                          <form.AppField
                                            name={`inters[${item.originalIndex}].verificationNotes`}
                                            validators={
                                              isComplete
                                                ? {
                                                    onBlur: missionAssessmentInterCreateSchema.shape.verificationNotes,
                                                  }
                                                : {}
                                            }
                                          >
                                            {(verificationNotesField) => (
                                              <FormField>
                                                <FormLabel
                                                  label="Verification Notes"
                                                  className="w-auto"
                                                />
                                                <verificationNotesField.TextAreaField
                                                  placeholder="Notes about verification"
                                                  rows={2}
                                                />
                                              </FormField>
                                            )}
                                          </form.AppField>
                                        </div>
                                      </AccordionContent>
                                    </AccordionItem>

                                    {/* Integrations Section */}
                                    <AccordionItem
                                      value="integrations"
                                      className="border rounded-md bg-white"
                                    >
                                      <AccordionTrigger className="px-4 hover:no-underline">
                                        <span className="text-sm font-medium">Integrations</span>
                                      </AccordionTrigger>
                                      <AccordionContent className="px-4 pb-4">
                                        <div className="grid gap-4 md:grid-cols-2">
                                          <form.AppField name={`inters[${item.originalIndex}].digitalIntegrationIds`}>
                                            {() => (
                                              <SelectMultipleField
                                                label="Digital Integrations"
                                                placeholder="Select digital integrations"
                                                options={digitalIntegrationOptions}
                                                disabled={isLoadingDigitalIntegrations}
                                              />
                                            )}
                                          </form.AppField>

                                          <form.AppField
                                            name={`inters[${item.originalIndex}].operationalIntegrationIds`}
                                          >
                                            {() => (
                                              <SelectMultipleField
                                                label="Operational Integrations"
                                                placeholder="Select operational integrations"
                                                options={operationalIntegrationOptions}
                                                disabled={isLoadingOperationalIntegrations}
                                              />
                                            )}
                                          </form.AppField>

                                          <form.AppField name={`inters[${item.originalIndex}].physicalIntegrationIds`}>
                                            {() => (
                                              <SelectMultipleField
                                                label="Physical Integrations"
                                                placeholder="Select physical integrations"
                                                options={physicalIntegrationOptions}
                                                disabled={isLoadingPhysicalIntegrations}
                                              />
                                            )}
                                          </form.AppField>
                                        </div>
                                      </AccordionContent>
                                    </AccordionItem>
                                  </Accordion>
                                </div>

                                <IconButton
                                  ariaLabel="Remove item"
                                  icon={<XIcon size={16} />}
                                  onClick={() => field.removeValue(item.originalIndex)}
                                  size="lg"
                                  variant="secondary"
                                />
                              </div>
                            </div>
                          )}
                        </form.Subscribe>
                      );
                    })}

                    {technologyId && isAIEnabled && !getIsGenerating(`inter_${section.type}`) && (
                      <MissionAssessmentInteroperabilitySuggestion
                        categoryOptions={categoryOptions}
                        data={
                          (getSuggestion(`inter_${section.type}`) as
                            | MissionAssessmentInterAiGenerationDataDto
                            | undefined) || null
                        }
                        isGenerating={false}
                        model={getSuggestionModel(`inter_${section.type}`)}
                        onAccept={() => handleAcceptJsonSuggestion(section.type)}
                        onReject={() => rejectSuggestion(`inter_${section.type}`)}
                      />
                    )}

                    <section className="flex items-center gap-2">
                      {!getIsGenerating(`inter_${section.type}`) && (
                        <Button
                          type="button"
                          variant="link"
                          onClick={() =>
                            field.pushValue({
                              categoryId: '',
                              country: undefined,
                              description: '',
                              digitalIntegrationIds: [],
                              fieldedStatus: undefined,
                              mrl: undefined,
                              name: '',
                              notes: '',
                              operationalIntegrationIds: [],
                              physicalIntegrationIds: [],
                              priorityRationale: undefined,
                              priorityScore: undefined,
                              reference: undefined,
                              subcategoryId: '',
                              trl: undefined,
                              type: section.type,
                              verificationConfidence: undefined,
                              verificationNotes: undefined,
                            })
                          }
                          className="h-auto self-start"
                        >
                          <PlusIcon className="size-4" />
                          Add Technology
                        </Button>
                      )}

                      {technologyId && isAIEnabled && (
                        <>
                          {!getIsGenerating(`inter_${section.type}`) && <div>or</div>}

                          <GenerateJSONButton<
                            MissionAssessmentInterAiQueryDto,
                            MissionAssessmentInterAiGenerationResponseDto
                          >
                            buttonProps={{
                              children: (
                                <>
                                  <SparklesIcon className="size-4" />
                                  Generate
                                </>
                              ),
                              variant: 'link',
                            }}
                            description={
                              <>
                                Use Tyr's AI assistant to generate a complete {section.type.toLowerCase()} assessment.
                                All relevant metadata and context will be used.
                              </>
                            }
                            chatApiEndpoint={MISSION_ASSESSMENT_AI_ENDPOINTS.chat}
                            chatData={{
                              interType: section.type,
                              objectKind: MissionAssessmentChatObjectKind.INTER,
                              technologyId,
                            }}
                            isGenerating={getIsGenerating(`inter_${section.type}`)}
                            mutateAsync={generateInter}
                            onAcceptData={(value) =>
                              applyGeneratedInter(section.type, parseGeneratedInterData(value, section.type))
                            }
                            onGeneratingEnd={handleGeneratingEndCallback(section.type)}
                            onGeneratingStart={handleGeneratingStartCallback(section.type)}
                            onSuccess={handleGenerationSuccess(section.type)}
                            params={{
                              technologyId,
                              type: section.type,
                            }}
                            renderDataArtifact={(value) => (
                              <MissionAssessmentInterSuggestionFields
                                categoryOptions={categoryOptions}
                                data={parseGeneratedInterData(value, section.type)}
                              />
                            )}
                            title={
                              section.type === MissionAssessmentInterType.INTEROPERABLE
                                ? 'Interoperable Technology'
                                : 'Interchangeable Technology'
                            }
                          />
                        </>
                      )}
                    </section>
                  </div>
                );
              }}
            </form.AppField>
          </div>
        ))}
      </div>
    );
  },
});
